import { PaymentPlan, PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import Styles from './PlanColumn.module.scss'
import { PlanIcon } from 'components/Settings/TieredBilling/components/PlanIcon/PlanIcon'
import cx from 'classnames'
import { Button } from 'blaze-ui/Button'
import { useMemo, useState } from 'react'
import { PlanFeatureRow, PlanFeatureRowProps } from '../PlanFeatureRow/PlanFeatureRow'
import { pluralize } from 'utils'
import { Approval, ArrowSkip, Brand, Calendar2, Hash2, Knowledge, LightningFilled, MessageText2 } from 'blaze-ui/icons'
import Iphone02 from 'blaze-ui/icons/16/Iphone02'
import { BlazeAI } from 'almanac-ui/icons'
import FileSearch1 from 'blaze-ui/icons/20/FileSearch1'
import UserProfile1 from 'blaze-ui/icons/20/UserProfile1'
import PhoneCall01 from 'blaze-ui/icons/16/PhoneCall01'
import FileCheck02 from 'blaze-ui/icons/16/FileCheck02'
import Campaign from 'blaze-ui/icons/16/Campaign'
import { ArrowRightSquareContained } from 'blaze-ui/icons/20/ArrowRightSquareContained'
import Bezier01 from 'blaze-ui/icons/16/Bezier01'

interface Props {
  currentPlanKey: PaymentPlanKey
  currentPlanCancelAt?: string
  currentBillingFrequency: 'month' | 'year'
  plan: PaymentPlan
  billingFrequency: 'month' | 'year'
  onPlanSwitchClick?: (priceKey: string) => void
  disabled?: boolean
}

// Used to determine what is an upgrade or downgrade
const planRanking: Record<string, number> = {
  [PaymentPlanKey.BlazeCreator]: 1,
  [PaymentPlanKey.BlazePro]: 2,
  [PaymentPlanKey.BlazeTeam]: 3,
  [PaymentPlanKey.BlazeAgency]: 4,
}

export const PlanColumn = ({
  plan,
  billingFrequency,
  currentPlanKey,
  currentPlanCancelAt,
  onPlanSwitchClick,
  currentBillingFrequency,
}: Props) => {
  const [processing, setProcessing] = useState(false)
  const buttonText = useMemo(() => {
    if (plan.key === currentPlanKey) {
      if (currentPlanCancelAt) return 'Reactivate'
      if (billingFrequency !== currentBillingFrequency) return 'Switch to this plan'
      return 'Your Current Plan'
    }
    if (planRanking[plan.key] === planRanking[currentPlanKey] + 1) return 'Upgrade to this plan'
    if (planRanking[plan.key] > planRanking[currentPlanKey]) return 'Upgrade'
    if (planRanking[plan.key] > planRanking[currentPlanKey]) return 'Upgrade'
    return 'Downgrade'
  }, [currentPlanKey, plan.key, billingFrequency, currentBillingFrequency, currentPlanCancelAt])

  const featureHeaderText = useMemo(() => {
    switch (plan.key) {
      case PaymentPlanKey.BlazePro:
        return 'Everything from Creator and:'
      case PaymentPlanKey.BlazeTeam:
        return 'Everything from Pro and:'
      case PaymentPlanKey.BlazeAgency:
        return 'Everything from Startup and:'
      default:
        return 'Includes:'
    }
  }, [plan.key])

  return (
    <div className={cx(Styles.container, Styles[plan.key])}>
      <div className={Styles.header}>
        <div className={Styles.headerLeft}>
          <PlanIcon size={20} plan={plan.key} />
          <h4>{plan.name}</h4>
        </div>
        <div className={Styles.headerRight}>
          {billingFrequency === 'month' ? `$${plan.price.month.unit_amount}/mo` : `$${plan.price.year.unit_amount}/yr`}
        </div>
      </div>
      <div className={Styles.body}>
        <Button
          isDisabled={
            (plan.key === currentPlanKey && currentBillingFrequency === billingFrequency && !currentPlanCancelAt) ||
            processing
          }
          variant={planRanking[plan.key] === planRanking[currentPlanKey] + 1 ? 'primary' : 'tertiary'}
          fullWidth
          onPress={async () => {
            setProcessing(true)
            await onPlanSwitchClick?.(
              billingFrequency === 'month' ? plan.price.month.price_id : plan.price.year.price_id,
            )
            setProcessing(false)
          }}
        >
          {processing ? 'Processing' : buttonText}
        </Button>
        <p className={Styles.featuresLabel}>{featureHeaderText}</p>
        <PlanFeatureRow
          limit={plan.base_price_meta.seats_limit}
          icon={<UserProfile1 size={16} />}
          feature={pluralize('Seat', parseInt(`${plan.base_price_meta.seats_limit}`) || 0)}
        />
        <PlanFeatureRow
          limit={plan.base_price_meta.brand_kits_limit}
          icon={<Brand size={16} />}
          feature={'Brand Kits'}
        />
        <PlanFeatureRow
          limit={plan.base_price_meta.integrations_limit}
          icon={<Iphone02 size={16} />}
          feature={'Accounts per Platform'}
        />
        <PlanFeatureRow
          limit={plan.base_price_meta.published_posts_limit}
          icon={<LightningFilled size={16} />}
          feature={'Published Posts per Platform'}
        />
        <PlanFeatureRow
          limit={
            plan.base_price_meta.ai_words_limit === 'unlimited'
              ? 'Unlimited'
              : `${parseInt(`${plan.base_price_meta.ai_words_limit}`) / 1000}K`
          }
          icon={<BlazeAI size={16} />}
          feature={'AI Words'}
        />
        <PlanFeatureRow
          limit={plan.base_price_meta.plagiarism_checks_limit}
          icon={<FileSearch1 size={16} />}
          feature={'Plagiarism Scans'}
        />
        <PlanFeatureRow
          limit={plan.base_price_meta.source_materials_limit}
          icon={<Knowledge size={16} />}
          feature={'Source Materials'}
        />
        {extraPlanFeatures[plan.key].map((feature) => (
          <PlanFeatureRow key={feature.feature} {...feature} />
        ))}
      </div>
    </div>
  )
}

const extraPlanFeatures: Record<string, PlanFeatureRowProps[]> = {
  blaze_creator: [
    { icon: <PhoneCall01 size={16} />, feature: 'Unlimited docs & designs' },
    { icon: <UserProfile1 size={16} />, feature: 'Unlimited guest access' },
    { icon: <Calendar2 size={16} />, feature: 'Social media calendar' },
    { icon: <Campaign size={16} />, feature: 'Multi-channel campaign' },
    { icon: <FileCheck02 size={16} />, feature: 'Unlimited SEO checks' },
  ],
  blaze_pro: [
    { icon: <Hash2 size={16} />, feature: 'Link & hashtag banks' },
    { icon: <ArrowRightSquareContained size={16} />, feature: 'Multiple post wizards' },
    { icon: <PhoneCall01 size={16} />, feature: 'Priority support' },
    { icon: <ArrowSkip size={16} />, feature: 'Priority AI processing' },
  ],
  blaze_team: [
    { icon: <MessageText2 size={16} />, feature: 'Real time collaboration, Comments & Tasks' },
    { icon: <Approval size={16} />, feature: 'Content approval tools' },
  ],
  blaze_agency: [
    { icon: <PhoneCall01 size={16} />, feature: 'Dedicated onboarding & workspace setup' },
    { icon: <Bezier01 size={16} />, feature: 'Multi-workspace volume discounts' },
  ],
}
